import { fetchCar3dModels, type Car3dModelItem } from '../cars/api.js';
import { mountLazyCarOrientationEmbed } from '../cars/carOrientationEmbedLazy.js';
import { applyRoleVisibility } from '../auth/navAccount.js';
import {
  attachAssetPickerScrollSentinel,
  type AssetPickerScrollSentinelHandle,
} from '../ui/assetPickerScroll.js';
import { createMultirxShineLoader } from '../ui/multirxShineLoader.js';
import { pageForIndex, sliceModelsPage } from './models3dPage.js';
import type { GaragePaneHandle, GaragePaneHosts } from './garageEmbed.js';

/**
 * Catalogue modèles 3D dans le shell garage :
 * liste filtrable à gauche + grille paginée (10) avec recadrage inline à droite.
 */
export function mountModels3dPane(hosts: GaragePaneHosts): GaragePaneHandle {
  const { listHost, detailHost, actionsHost, initialId, onSelectId } = hosts;

  listHost.innerHTML = '';
  detailHost.innerHTML = '';
  actionsHost.innerHTML = '';

  const toolbar = document.createElement('div');
  toolbar.className = 'garage-sidebar-toolbar-host';
  const toolbarInner = document.createElement('div');
  toolbarInner.className = 'garage-sidebar-toolbar assets-panel-filters';
  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'garage-toolbar-search';
  search.placeholder = 'Rechercher un modèle…';
  search.setAttribute('aria-label', 'Filtrer les modèles 3D');
  const countEl = document.createElement('p');
  countEl.className = 'garage-toolbar-count';
  countEl.setAttribute('aria-live', 'polite');
  toolbarInner.append(search, countEl);
  toolbar.appendChild(toolbarInner);

  const listInner = document.createElement('div');
  listInner.className = 'circuits-sidebar-inner garage-sidebar-list';

  const sidebar = document.createElement('div');
  sidebar.className = 'circuits-sidebar garage-sidebar';
  sidebar.append(toolbar, listInner);
  listHost.appendChild(sidebar);

  const scrollRoot = document.createElement('div');
  scrollRoot.className = 'garage-models-detail';
  const grid = document.createElement('div');
  grid.className = 'garage-models-grid';
  scrollRoot.appendChild(grid);
  detailHost.appendChild(scrollRoot);

  let allItems: Car3dModelItem[] = [];
  let filtered: Car3dModelItem[] = [];
  let selectedSlug: string | null = initialId?.trim() || null;
  let page = 1;
  let sentinelHandle: AssetPickerScrollSentinelHandle | null = null;
  const cardCleanups = new Map<string, () => void>();

  function clearCardCleanups(): void {
    for (const dispose of cardCleanups.values()) dispose();
    cardCleanups.clear();
  }

  function filteredList(): Car3dModelItem[] {
    const q = search.value.trim().toLowerCase();
    return q ? allItems.filter((it) => it.slug.toLowerCase().includes(q)) : allItems;
  }

  function updateCount(): void {
    countEl.textContent =
      filtered.length === allItems.length
        ? `${allItems.length} modèle${allItems.length !== 1 ? 's' : ''}`
        : `${filtered.length} / ${allItems.length} affichés`;
  }

  function refreshList(): void {
    listInner.innerHTML = '';
    if (filtered.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'circuits-empty';
      empty.style.padding = '1rem';
      empty.textContent =
        allItems.length === 0 ? 'Aucun modèle 3D disponible.' : 'Aucun modèle ne correspond.';
      listInner.appendChild(empty);
      return;
    }

    const list = document.createElement('div');
    list.className = 'garage-models-list';
    filtered.forEach((item) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = `garage-models-list__row${selectedSlug === item.slug ? ' is-selected' : ''}`;
      row.dataset.slug = item.slug;
      row.textContent = item.slug;
      row.addEventListener('click', () => selectSlug(item.slug));
      list.appendChild(row);
    });
    listInner.appendChild(list);
  }

  function createCard(item: Car3dModelItem): HTMLElement {
    const card = document.createElement('article');
    card.className = `garage-models-card${selectedSlug === item.slug ? ' is-selected' : ''}`;
    card.dataset.modelSlug = item.slug;

    const title = document.createElement('h3');
    title.className = 'garage-models-card__title';
    title.textContent = item.slug;

    const embedHost = document.createElement('div');
    embedHost.className = 'garage-models-card__embed';

    card.append(title, embedHost);
    cardCleanups.set(item.slug, mountLazyCarOrientationEmbed(embedHost, item, scrollRoot));
    return card;
  }

  function attachSentinel(): void {
    sentinelHandle?.disconnect();
    sentinelHandle = null;
    const meta = sliceModelsPage(filtered, page, { withMeta: true });
    sentinelHandle = attachAssetPickerScrollSentinel(
      grid,
      meta.hasMore,
      () => {
        page += 1;
        renderGrid({ appendOnly: true });
      },
      scrollRoot
    );
  }

  function renderGrid(opts?: { appendOnly?: boolean }): void {
    const meta = sliceModelsPage(filtered, page, { withMeta: true });
    const visible = meta.items;

    if (!opts?.appendOnly) {
      clearCardCleanups();
      sentinelHandle?.disconnect();
      sentinelHandle = null;
      grid.replaceChildren();
    }

    const already = new Set(
      [...grid.querySelectorAll<HTMLElement>('[data-model-slug]')].map(
        (el) => el.dataset.modelSlug!
      )
    );
    const toAdd = opts?.appendOnly ? visible.filter((it) => !already.has(it.slug)) : visible;

    if (filtered.length === 0) {
      clearCardCleanups();
      sentinelHandle?.disconnect();
      sentinelHandle = null;
      grid.replaceChildren();
      const empty = document.createElement('p');
      empty.className = 'garage-models-detail__empty';
      empty.textContent =
        allItems.length === 0
          ? 'Aucun modèle 3D disponible.'
          : 'Aucun modèle ne correspond à la recherche.';
      grid.appendChild(empty);
      return;
    }

    // Retirer l’éventuelle sentinelle avant d’ajouter des cartes
    grid.querySelector('.asset-picker-sentinel')?.remove();

    for (const item of toAdd) {
      grid.appendChild(createCard(item));
    }

    // Mettre à jour sélection visuelle
    for (const card of grid.querySelectorAll<HTMLElement>('.garage-models-card')) {
      card.classList.toggle('is-selected', card.dataset.modelSlug === selectedSlug);
    }

    applyRoleVisibility();
    attachSentinel();
  }

  function ensurePageForSlug(slug: string): void {
    const index = filtered.findIndex((it) => it.slug === slug);
    if (index < 0) return;
    const needed = pageForIndex(index);
    if (needed > page) {
      page = needed;
      renderGrid();
    }
  }

  function scrollToSlug(slug: string): void {
    ensurePageForSlug(slug);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const escaped =
          typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(slug)
            : slug.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        const card = grid.querySelector<HTMLElement>(`[data-model-slug="${escaped}"]`);
        card?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      });
    });
  }

  function selectSlug(slug: string): void {
    selectedSlug = slug;
    onSelectId?.(slug);
    refreshList();
    for (const card of grid.querySelectorAll<HTMLElement>('.garage-models-card')) {
      card.classList.toggle('is-selected', card.dataset.modelSlug === slug);
    }
    scrollToSlug(slug);
  }

  function applyFilter(): void {
    filtered = filteredList();
    page = 1;
    updateCount();
    refreshList();
    if (selectedSlug && !filtered.some((it) => it.slug === selectedSlug)) {
      // garde la sélection hash mais scroll seulement si encore dans le filtre
    }
    renderGrid();
    if (selectedSlug && filtered.some((it) => it.slug === selectedSlug)) {
      ensurePageForSlug(selectedSlug);
      scrollToSlug(selectedSlug);
    }
  }

  search.addEventListener('input', () => applyFilter());

  listInner.innerHTML = '<p class="circuits-loading" style="padding:1rem">Chargement…</p>';
  const bootLoader = createMultirxShineLoader('Chargement des modèles…');
  grid.replaceChildren(bootLoader);

  void fetchCar3dModels()
    .then((models) => {
      allItems = models;
      filtered = filteredList();
      updateCount();
      refreshList();
      page = 1;
      if (selectedSlug) {
        const exists = allItems.some((it) => it.slug === selectedSlug);
        if (!exists) selectedSlug = null;
        else
          page = Math.max(page, pageForIndex(filtered.findIndex((it) => it.slug === selectedSlug)));
      }
      renderGrid();
      if (selectedSlug) scrollToSlug(selectedSlug);
    })
    .catch((err) => {
      listInner.innerHTML = '';
      const errEl = document.createElement('p');
      errEl.className = 'circuits-error';
      errEl.style.padding = '1rem';
      errEl.textContent = err instanceof Error ? err.message : 'Erreur inconnue';
      listInner.appendChild(errEl);
      grid.replaceChildren();
      const detailErr = document.createElement('p');
      detailErr.className = 'garage-models-detail__empty';
      detailErr.textContent = err instanceof Error ? err.message : 'Erreur inconnue';
      grid.appendChild(detailErr);
    });

  return {
    dispose() {
      clearCardCleanups();
      sentinelHandle?.disconnect();
      sentinelHandle = null;
      listHost.innerHTML = '';
      detailHost.innerHTML = '';
      actionsHost.innerHTML = '';
    },
  };
}
